// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import HeaderLogged from "../components/HeaderLogged";
import Footer from "../components/Footer";
import IrsMaintenanceBanner from "./IrsMaintenanceBanner";
import CompanyDetailsContent from "./CompanyDetailsContent";

function Dashboard() {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // VIN search state
  const [vinCompanyIds, setVinCompanyIds] = useState(new Set());
  const [vinSearching, setVinSearching] = useState(false);

  const [balance, setBalance] = useState(0);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userError, setUserError] = useState(null);

  // Новые состояния для Submission History
  const [showHistory, setShowHistory] = useState(false);
  const [rangeMode, setRangeMode] = useState("preset"); // 'preset' | 'custom'
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Сохраним userId и email из профиля (для запроса и имени файла)
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState("");

  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // ---- helpers for dates (локально, без UTC-сдвигов) ----
  const pad2 = (n) => String(n).padStart(2, "0");
  const fmtLocalDate = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  // preset: Июнь–Август текущего года (жёстко задано в JS)
  const getJuneAugustRangeForYear = (year) => {
    // Июнь: месяц 5 (0-based), 1-е число
    const start = new Date(year, 5, 1);
    // Последний день августа: 0-е число сентября => август последний день
    const end = new Date(year, 8, 0);
    return {
      from: fmtLocalDate(start),
      to: fmtLocalDate(end),
    };
  };

  const applyPresetRange = () => {
    const y = new Date().getFullYear();
    const r = getJuneAugustRangeForYear(y);
    setDateFrom(r.from);
    setDateTo(r.to);
  };

  // Подгрузка баланса пользователя (+ id, email если есть)
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoadingUser(true);
        const response = await fetch("/api/users-logic/me/", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Error fetching user");
        const data = await response.json();
        if (typeof data.balance !== "undefined") setBalance(data.balance);
        if (typeof data.id !== "undefined") setUserId(data.id);
        if (typeof data.email !== "undefined") setUserEmail(data.email);
      } catch (err) {
        setUserError(err.message);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, [token]);

  // Подгрузка списка компаний
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await fetch("/api/companies/", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Error fetching companies");
        const data = await response.json();
        setCompanies(data);
      } catch (error) {
        setCompaniesError(error.message);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [token]);

  // При открытии окошка — если пресетный режим, заполним даты пресетом
  useEffect(() => {
    if (showHistory && rangeMode === "preset") {
      applyPresetRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory, rangeMode]);

  // VIN search with debounce
  useEffect(() => {
    const term = searchTerm.trim();
    // Минимум 3 символа для поиска по VIN
    if (term.length < 3) {
      setVinCompanyIds(new Set());
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        setVinSearching(true);
        const resp = await fetch(
          `/api/companies/search-by-vin/?vin=${encodeURIComponent(term)}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          },
        );
        if (resp.ok) {
          const data = await resp.json();
          const ids = new Set(data.map((c) => c.id));
          setVinCompanyIds(ids);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("VIN search error:", err);
        }
      } finally {
        setVinSearching(false);
      }
    }, 300); // debounce 300ms

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchTerm, token]);

  // Фильтрация по имени, EIN или VIN (без учёта регистра)
  const filteredCompanies = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return companies;
    return companies.filter((company) => {
      const nameOk = (company.name || "").toLowerCase().includes(term);
      const einOk = (company.EIN ? company.EIN.toLowerCase() : "").includes(
        term,
      );
      const vinOk = vinCompanyIds.has(company.id);
      return nameOk || einOk || vinOk;
    });
  }, [companies, searchTerm, vinCompanyIds]);

  // Скачивание истории поданной формы 2290
  const downloadSubmissionHistory = async () => {
    try {
      if (!userId) {
        alert(
          "Cannot determine user id. Make sure your profile endpoint returns id.",
        );
        return;
      }
      if (!dateFrom || !dateTo) {
        alert("Please select both Date From and Date To.");
        return;
      }
      if (dateFrom > dateTo) {
        alert("Date From must be before or equal to Date To.");
        return;
      }

      setDownloading(true);

      const params = new URLSearchParams({
        date_from: dateFrom,
        date_to: dateTo,
      }).toString();
      const resp = await fetch(`/api/export/form2290/${userId}/?${params}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resp.ok) {
        let msg = `Error downloading report: ${resp.status} ${resp.statusText}`;
        try {
          const maybeJson = await resp.json();
          if (maybeJson && maybeJson.detail) msg = maybeJson.detail;
        } catch (_) {}
        throw new Error(msg);
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeEmail = (userEmail || `user_${userId}`).replace(
        /[^a-zA-Z0-9._-]/g,
        "_",
      );
      a.download = `form2290_report_${safeEmail}_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <HeaderLogged />
      <main className="flex-1 bg-white">
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          {/* Верхний блок: заголовок и кнопки */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-xl font-semibold text-[#0F2C5E]">
              All Companies
            </h2>
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate("/create-company")}
                  className="bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition text-sm"
                >
                  Add New Business
                </button>
                <button
                  onClick={() => setShowHistory((v) => !v)}
                  className="bg-[#0F2C5E] text-white px-4 py-2 hover:bg-[#0F2C5E]/80 transition text-sm"
                >
                  Submission History
                </button>
              </div>

              {/* Окошко для выбора периода (пресет/кастом) */}
              {showHistory && (
                <div className="w-full sm:w-auto bg-white border border-gray-300 shadow-sm p-3 rounded">
                  {/* Переключатель режима */}
                  <div className="flex items-center gap-4 mb-3">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="rangeMode"
                        value="preset"
                        checked={rangeMode === "preset"}
                        onChange={() => setRangeMode("preset")}
                      />
                      Preset: June–August (current year)
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="rangeMode"
                        value="custom"
                        checked={rangeMode === "custom"}
                        onChange={() => setRangeMode("custom")}
                      />
                      Custom dates
                    </label>
                  </div>

                  {/* Если пресет — покажем выбранные даты (readonly). Если кастом — дадим выбрать. */}
                  {rangeMode === "preset" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-600 mb-1">
                          Date From
                        </label>
                        <input
                          type="date"
                          value={dateFrom}
                          readOnly
                          className="border border-gray-300 p-2 text-sm bg-gray-100 text-gray-700"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-600 mb-1">
                          Date To
                        </label>
                        <input
                          type="date"
                          value={dateTo}
                          readOnly
                          className="border border-gray-300 p-2 text-sm bg-gray-100 text-gray-700"
                        />
                      </div>
                      <div className="flex">
                        <button
                          onClick={downloadSubmissionHistory}
                          disabled={downloading}
                          className={`w-full sm:w-auto bg-[#0F2C5E] text-white px-4 py-2 text-sm transition ${
                            downloading
                              ? "opacity-60 cursor-not-allowed"
                              : "hover:bg-[#0F2C5E]/80"
                          }`}
                        >
                          {downloading ? "Downloading…" : "Get History"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-600 mb-1">
                          Date From
                        </label>
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          className="border border-gray-300 p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-600 mb-1">
                          Date To
                        </label>
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          className="border border-gray-300 p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
                        />
                      </div>
                      <div className="flex">
                        <button
                          onClick={downloadSubmissionHistory}
                          disabled={downloading}
                          className={`w-full sm:w-auto bg-[#0F2C5E] text-white px-4 py-2 text-sm transition ${
                            downloading
                              ? "opacity-60 cursor-not-allowed"
                              : "hover:bg-[#0F2C5E]/80"
                          }`}
                        >
                          {downloading ? "Downloading…" : "Get History"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Подсказка про пользователя (если id не найден) */}
                  {!userId && (
                    <p className="text-xs text-amber-700 mt-2">
                      Note: unable to detect user id from /api/users-logic/me/.
                      Update backend to include id/email.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Строка поиска под заголовком и кнопками */}
          <div className="mt-4 relative">
            <input
              type="text"
              placeholder="Search by company name, EIN or VIN"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E] bg-white text-sm"
            />
            {vinSearching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                Searching VIN...
              </span>
            )}
          </div>

          {/* Вставка баланса и ссылки на профиль */}
          <div className="flex justify-between items-center text-sm text-gray-700 my-2">
            {loadingUser ? (
              <span>Loading rewards...</span>
            ) : userError ? (
              <span className="text-red-500">{userError}</span>
            ) : (
              <span>
                Your rewards: <strong>${balance}</strong>
              </span>
            )}
            <Link to="/user" className="underline text-[#0F2C5E]">
              Withdrawal, History & Profile
            </Link>
          </div>

          <hr className="border-t border-gray-300" />

          {/* Список компаний / детали единственной компании */}
          {loadingCompanies ? (
            <div className="text-gray-600">Loading...</div>
          ) : companiesError ? (
            <div className="text-red-500 text-sm">{companiesError}</div>
          ) : companies.length === 1 ? (
            <CompanyDetailsContent companyId={companies[0].id} />
          ) : filteredCompanies.length === 0 ? (
            <div className="text-center mt-8 text-gray-600 text-sm">
              <p>No companies match your search.</p>
              <Link to="/create-company" className="text-[#0F2C5E] underline">
                Create a company
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCompanies.map((company, index) => {
                const rowBgColor =
                  index % 2 === 0 ? "bg-gray-100" : "bg-blue-100";

                const term = searchTerm.trim();
                const shouldHighlightByVin =
                  term.length >= 3 && vinCompanyIds.has(company.id);

                const companyUrl = shouldHighlightByVin
                  ? `/company/${company.id}?vin=${encodeURIComponent(term)}`
                  : `/company/${company.id}`;

                return (
                  <div
                    key={company.id}
                    className={`flex flex-col sm:flex-row items-start sm:items-center ${rowBgColor} p-3 border-b border-gray-200 cursor-pointer`}
                    onClick={() => navigate(companyUrl)}
                  >
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <span className="font-medium text-[#0F2C5E]">
                        {company.name}
                      </span>
                      <span className="text-sm text-gray-600">
                        EIN: {company.EIN || "—"}
                      </span>
                      <span className="text-sm text-gray-600">
                        Address: {company.state}, {company.city}
                      </span>
                    </div>
                    <div className="flex space-x-2 mt-2 sm:mt-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/company/${company.id}/edit`);
                        }}
                        className="bg-gray-800 text-white px-3 py-1 text-sm hover:bg-gray-900 transition"
                      >
                        Edit info
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/company/${company.id}/file-form`);
                        }}
                        className="bg-[#0F2C5E] text-white px-3 py-1 text-sm hover:bg-[#0F2C5E]/80 transition"
                      >
                        File form
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(companyUrl);
                        }}
                        className="bg-[#0F2C5E] text-white px-3 py-1 text-sm hover:bg-[#0F2C5E]/80 transition"
                      >
                        Select
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Dashboard;
